import { CloseIcon } from '@protector/icons';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import { COLOR_OPTIONS } from 'config/style';
import STTypo, { STTypoModels } from 'core/shared/typo';
import { parseFeatureValues } from '@features/regional-overview/regional-risk/utils/popup.utils';
import type { RegionalOverviewV2Properties, RiskLevelEnum } from 'querys/regional-overview/regional-overview.query.models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RiskTag } from '../field-details-card/field-details-card.styles';
import { getRiskLabel } from '../field-details-card/field-details-card.utils';
import { CloseButton } from './cluster-popup-card.styles';
import { sortFieldsByRiskFarmAndName } from './cluster-popup-card.utils';
import styles from './cluster-fields-content.module.css';

export interface ClusterFieldsContentProps {
  clusterLeaves: RegionalOverviewV2Properties[];
  onClose: () => void;
  onFieldClick?: (fieldId: string) => void;
  inSheet?: boolean;
}

export const ClusterFieldsContent = ({ clusterLeaves, onClose, onFieldClick, inSheet = false }: ClusterFieldsContentProps) => {
  const [t] = useTranslation();

  const sortedFields = useMemo(() => {
    const parsed = [...clusterLeaves].map(leaf => parseFeatureValues(leaf) as RegionalOverviewV2Properties);
    return parsed.sort(sortFieldsByRiskFarmAndName);
  }, [clusterLeaves]);

  return (
    <>
      <header className={classNames(styles.header, { [styles.headerDivider]: inSheet })}>
        <STTypo
          type={inSheet ? STTypoModels.TypeOptions.P1 : STTypoModels.TypeOptions.P2}
          fontWeight={600}
          color={COLOR_OPTIONS.NEUTRAL_80}
          fontFamily='Noto Sans'
          lineHeight={inSheet ? '20px' : undefined}
          customClassName={inSheet ? styles.clusterTitle : undefined}>
          {t('pages.regional_overview.cluster_popup.fields_clustered', { count: sortedFields.length })}
        </STTypo>
        <CloseButton
          type='text'
          onPointerDown={event => event.stopPropagation()}
          onClick={onClose}
          icon={<CloseIcon />}
          aria-label={t('pages.regional_overview.cluster_popup.close')}
        />
      </header>
      <ul className={styles.fieldList}>
        {sortedFields.map(field => {
          const farmName = field?.values?.property_name ?? '';
          const riskLevel = field.calculatedRisk as RiskLevelEnum;

          return (
            <li key={field.id} className={styles.fieldListRow}>
              <button type='button' className={styles.fieldItem} onClick={() => onFieldClick?.(field.id)}>
                <section>
                  <Tooltip title={farmName}>
                    <span className={styles.ellipsis}>
                      <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_70} fontFamily='Noto Sans'>
                        {farmName}/
                      </STTypo>
                    </span>
                  </Tooltip>
                  <Tooltip title={field.name}>
                    <span className={styles.ellipsis}>
                      <STTypo type={STTypoModels.TypeOptions.P1} fontWeight={600} color={COLOR_OPTIONS.NEUTRAL_80} fontFamily='Noto Sans'>
                        {field.name}
                      </STTypo>
                    </span>
                  </Tooltip>
                </section>
                <RiskTag $riskLevel={riskLevel}>{getRiskLabel(riskLevel, t)}</RiskTag>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
};
